import logger from '@/common/utils/logger';
import i18n from './index';
import en from './en';
import zh from './zh';
import zhTw from './zh-tw';
import ja from './ja';
import ko from './ko';
import ms from './ms';
import pt from './pt';
import ru from './ru';
import th from './th';
import vi from './vi';
import ar from './ar';
import es from './es';
import { getLocaleFromUrl, normalizeLocale } from './locale-from-url';

export { getLocaleFromUrl, normalizeLocale };

interface IpInfo {
  status: string;
  countryCode?: string;
}

/** 国家 → 语言；中国大陆用简体 zh（勿映射到 zh-tw） */
const countryToLocaleMap: Record<string, string> = {
  CN: 'zh',
  HK: 'zh-tw',
  TW: 'zh-tw',
  JP: 'ja',
  KR: 'ko',
  MY: 'ms',
  BR: 'pt',
  PT: 'pt',
  RU: 'ru',
  TH: 'th',
  VN: 'vi',
  SA: 'ar',
  AE: 'ar',
  ES: 'es',
  MX: 'es',
};

const DEFAULT_LOCALE = 'en';

/** 全部语言包已随主包静态引入，切换时只改 locale，避免动态 import 的 .default 解包失败导致整包回退英文 */
const localeMessages: Record<string, Record<string, unknown>> = {
  en,
  zh,
  'zh-tw': zhTw,
  ja,
  ko,
  ms,
  pt,
  ru,
  th,
  vi,
  ar,
  es,
};

export const supportedLocales = Object.keys(localeMessages);

function resolveMessages(locale: string): Record<string, unknown> {
  return localeMessages[locale] || localeMessages[DEFAULT_LOCALE];
}

async function getCountryCodeByIP(): Promise<string | null> {
  try {
    const res = await uni.request({
      url: 'http://ip-api.com/json',
      method: 'GET',
      timeout: 5000,
    });
    if (res.statusCode === 200 && res.data && (res.data as IpInfo).status === 'success') {
      return (res.data as IpInfo).countryCode || null;
    }
    logger.error('Failed to fetch country code, status:', res.statusCode);
    return null;
  } catch (error) {
    logger.error('Failed to fetch country code by IP:', error);
    return null;
  }
}

type SyncLocaleResult = { locale: string; /** 仅默认值时才允许后台 IP 覆盖 */ allowIpRefine: boolean };

/** 同步路径：URL / 本地缓存 / 系统语言，不碰网络 */
function determineLocaleSync(): SyncLocaleResult {
  try {
    const fromUrl = getLocaleFromUrl();
    if (fromUrl) {
      uni.setStorageSync('locale', fromUrl);
      logger.info(`[Language] Using locale from URL: ${fromUrl}`);
      return { locale: fromUrl, allowIpRefine: false };
    }

    const savedLocale = uni.getStorageSync('locale');
    if (savedLocale && supportedLocales.includes(savedLocale)) {
      logger.info(`[Language] Using saved locale from storage: ${savedLocale}`);
      return { locale: savedLocale, allowIpRefine: false };
    }

    try {
      const lang = uni.getSystemInfoSync().language.toLowerCase();
      let system: string | null = null;
      if (lang.startsWith('zh-hans')) system = 'zh';
      else if (lang.startsWith('zh-hant') || lang.startsWith('zh-tw') || lang.startsWith('zh-hk')) {
        system = 'zh-tw';
      } else if (lang.startsWith('zh')) system = 'zh';
      else {
        const simplified = lang.split('-')[0];
        if (supportedLocales.includes(simplified)) system = simplified;
      }
      if (system) {
        // 落盘，避免首屏系统语种被后台 IP 再改一次造成闪切
        uni.setStorageSync('locale', system);
        logger.info(`[Language] Using system locale: ${system}`);
        return { locale: system, allowIpRefine: false };
      }
    } catch {
      /* ignore */
    }
  } catch (error) {
    logger.error('[Language] Sync locale failed, using default:', error);
  }
  return { locale: DEFAULT_LOCALE, allowIpRefine: true };
}

/** 无 URL/本地/系统信号时后台 IP 探测；不阻塞首屏 */
async function refineLocaleByIpIfNeeded(current: string): Promise<void> {
  try {
    const saved = uni.getStorageSync('locale');
    if (saved && supportedLocales.includes(saved)) return;

    const countryCode = await getCountryCodeByIP();
    const again = uni.getStorageSync('locale');
    if (again && supportedLocales.includes(again)) return;

    const locale = (countryCode && countryToLocaleMap[countryCode]) || current || DEFAULT_LOCALE;
    uni.setStorageSync('locale', locale);
    if (locale !== current) {
      logger.info(`[Language] IP refine: country=${countryCode}, ${current} → ${locale}`);
      applyLocale(locale);
    }
  } catch (error) {
    logger.error('[Language] IP refine failed:', error);
    try {
      if (!uni.getStorageSync('locale')) uni.setStorageSync('locale', current);
    } catch {
      /* ignore */
    }
  }
}

function applyLocale(locale: string): void {
  const messages = resolveMessages(locale);
  i18n.global.setLocaleMessage(locale, messages);
  i18n.global.locale.value = locale;
}

export async function configureLanguage(): Promise<string> {
  const { locale, allowIpRefine } = determineLocaleSync();
  applyLocale(locale);
  logger.info(`[Language] Language configured to: ${locale}`);
  // 仅无明确信号时后台 IP；有系统/本地/URL 则不再改，避免闪切
  if (allowIpRefine) void refineLocaleByIpIfNeeded(locale);
  return locale;
}

export async function setLanguage(newLocale: string): Promise<void> {
  if (!supportedLocales.includes(newLocale)) {
    logger.warn(`[Language] Unsupported locale: ${newLocale}`);
    return;
  }
  if (i18n.global.locale.value === newLocale) return;

  applyLocale(newLocale);
  uni.setStorageSync('locale', newLocale);
  logger.info(`[Language] Switched and saved locale: ${newLocale}`);
}
